/**
 * Revenue Calculation Service
 * Handles all revenue metrics and schedule generation calculations
 */

import { CrmApiRepository } from './crm/CrmApiRepository.js';
import { ApiClient } from './ApiClient.js';
import { aggregateRevenueMetrics, scheduleInPeriod } from '../utils/ForecastUtils.js';
import { DashboardMetricsService } from './DashboardMetricsService.js';
import { getNextInvoiceDate, countInvoices } from '../utils/InvoiceFrequency.js';
import { formatDateLocal } from '../utils/Utils.js';
import { paymentModelToFrequency } from '../constants/PaymentModels.js';

export class RevenueCalculationService {
    static generateScheduleForDeal(deal) {
        const model = deal.paymentModel || 'monthly';
        if (model === 'ongoing') {
            if (Array.isArray(deal.ongoingScheduleRows) && deal.ongoingScheduleRows.length) {
                return RevenueCalculationService.generateRecurringSchedule({
                    ...deal,
                    recurringScheduleRows: deal.ongoingScheduleRows
                });
            }
            if (Array.isArray(deal.customScheduleRows) && deal.customScheduleRows.length) {
                return RevenueCalculationService.generateCustomSchedule(deal);
            }
            return [];
        }
        if (model === 'milestone' && Array.isArray(deal.dealMilestones) && deal.dealMilestones.length) {
            return RevenueCalculationService.generateMilestoneSchedule(deal);
        }
        if (model === 'custom' && Array.isArray(deal.customScheduleRows) && deal.customScheduleRows.length) {
            return RevenueCalculationService.generateCustomSchedule(deal);
        }
        // For one-time and recurring models, use custom schedule rows as source of truth
        if (Array.isArray(deal.customScheduleRows) && deal.customScheduleRows.length > 0) {
            return RevenueCalculationService.generateCustomSchedule(deal);
        }
        // Fallback: use recurring schedule rows
        if (Array.isArray(deal.recurringScheduleRows) && deal.recurringScheduleRows.length > 0) {
            return RevenueCalculationService.generateRecurringSchedule(deal);
        }
        const frequency = deal.invoiceFrequency || paymentModelToFrequency(model);
        return RevenueCalculationService.generateSchedule({ ...deal, invoiceFrequency: frequency });
    }

    static generateRecurringSchedule(deal) {
        const ts = new Date().toISOString();
        return (deal.recurringScheduleRows || []).map((row, i) => ({
            id: `${deal.id}-rs-${i}-${row.date || i}`,
            dealId: deal.id,
            invoiceDate: row.date,
            plannedAmount: Number(row.amount) || 0,
            actualAmount: 0,
            paidAmount: 0,
            status: 'planned',
            createdAt: ts,
            updatedAt: ts
        }));
    }

    static generateCustomSchedule(deal) {
        const ts = new Date().toISOString();
        const rows = deal.customScheduleRows || [];
        if (rows.length === 0 && deal.paymentModel === 'one_time' && deal.oneTimeDate) {
            return [{
                id: `${deal.id}-ot-0`,
                dealId: deal.id,
                invoiceDate: deal.oneTimeDate,
                plannedAmount: Number(deal.oneTimeAmount || deal.contractValue || 0),
                actualAmount: 0,
                paidAmount: 0,
                status: 'planned',
                createdAt: ts,
                updatedAt: ts
            }];
        }
        return rows.map((row, i) => ({
            id: `${deal.id}-cs-${i}-${row.invoiceDate || i}`,
            dealId: deal.id,
            invoiceDate: row.invoiceDate || row.date,
            plannedAmount: Number(row.amount) || 0,
            actualAmount: 0,
            paidAmount: 0,
            status: 'planned',
            createdAt: ts,
            updatedAt: ts
        }));
    }

    static generateMilestoneSchedule(deal) {
        const ts = new Date().toISOString();
        return (deal.dealMilestones || []).map((m, i) => ({
            id: `${deal.id}-ms-${i}-${m.dueDate || i}`,
            dealId: deal.id,
            invoiceDate: m.dueDate,
            plannedAmount: Number(m.amount) || 0,
            milestoneTitle: m.title || `Milestone ${i + 1}`,
            paymentTerms: m.paymentTerms || deal.paymentTerms || null,
            actualAmount: 0,
            paidAmount: 0,
            status: 'planned',
            createdAt: ts,
            updatedAt: ts
        }));
    }

    static validateMilestones(deal) {
        const contract = deal.contractValue || deal.dealAmount || deal.amount || 0;
        const milestones = deal.dealMilestones || [];
        const sum = milestones.reduce((s, m) => s + (Number(m.amount) || 0), 0);
        if (!milestones.length) return { valid: true, warning: 'Add at least one milestone.', sum };
        if (sum > contract + 0.01) {
            return { valid: false, message: `Milestone amounts ($${sum.toFixed(2)}) exceed contract value ($${contract.toFixed(2)}).`, sum };
        }
        if (Math.abs(sum - contract) > 0.01) {
            return { valid: true, warning: `Milestone amounts ($${sum.toFixed(2)}) differ from contract value ($${contract.toFixed(2)}).`, sum };
        }
        return { valid: true, sum };
    }

    static validateCustomSchedule(deal) {
        const contract = deal.contractValue || deal.dealAmount || deal.amount || 0;
        const rows = deal.customScheduleRows || [];
        const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        if (!rows.length) return { valid: true, warning: 'Add at least one invoice row.', sum };
        if (sum > contract + 0.01) {
            return { valid: false, message: `Custom schedule ($${sum.toFixed(2)}) exceeds contract value ($${contract.toFixed(2)}).`, sum };
        }
        if (Math.abs(sum - contract) > 0.01) {
            return { valid: true, warning: `Custom schedule ($${sum.toFixed(2)}) differs from contract value ($${contract.toFixed(2)}).`, sum };
        }
        return { valid: true, sum };
    }

    static validateOngoingSchedule(deal) {
        const rows = deal.ongoingScheduleRows || deal.customScheduleRows || [];
        const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
        const retainer = Number(deal.retainerAmount) || 0;
        if (!rows.length) {
            return { valid: true, warning: 'Add billing dates to generate a payment schedule.', sum };
        }
        if (sum <= 0 && retainer <= 0) {
            return { valid: false, message: 'Enter a retainer or schedule amounts.', sum };
        }
        return { valid: true, sum };
    }

    /**
     * Generate revenue schedule based on deal parameters
     */
    static generateSchedule(deal) {
        const schedules = [];
        const firstDate = new Date(deal.expectedFirstInvoiceDate);
        const lastDate = new Date(deal.lastInvoiceDate);
        const amount = Number(deal.invoiceAmount) || 0;

        let currentDate = new Date(firstDate);

        while (currentDate <= lastDate) {
            const scheduleId = `${deal.id}-${currentDate.toISOString().split('T')[0]}`;

            schedules.push({
                id: scheduleId,
                dealId: deal.id,
                invoiceDate: currentDate.toISOString().split('T')[0],
                plannedAmount: amount,
                actualAmount: 0,
                paidAmount: 0,
                status: 'planned',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            currentDate = getNextInvoiceDate(currentDate, deal.invoiceFrequency);
        }

        return schedules;
    }

    static validateRecurringContract(deal) {
        const model = deal.paymentModel || 'monthly';
        if (!['monthly', 'weekly', 'quarterly', 'annual'].includes(model)) {
            return { valid: true };
        }
        if (!deal.lastInvoiceDate) {
            return { valid: true };
        }
        const contract = Number(deal.contractValue ?? deal.dealAmount ?? deal.amount ?? 0);
        const metrics = RevenueCalculationService.calculateDealMetrics(deal);
        if (metrics.totalInvoices == null) {
            return { valid: true };
        }
        const expected = metrics.totalInvoices * (Number(deal.invoiceAmount) || 0);
        if (Math.abs(expected - contract) > 0.01) {
            return {
                valid: true,
                warning: `Invoice total ($${expected.toFixed(2)}) differs from contract value ($${contract.toFixed(2)}).`
            };
        }
        return { valid: true };
    }

    /**
     * Calculate contract value and total invoices
     */
    static calculateDealMetrics(deal) {
        const model = deal.paymentModel || 'monthly';

        if (model === 'ongoing') {
            const rows = deal.ongoingScheduleRows || deal.customScheduleRows || [];
            const contractValue = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0)
                || Number(deal.contractValue) || Number(deal.dealAmount) || Number(deal.amount) || 0;
            return {
                totalInvoices: rows.length || null,
                contractValue,
                openEnded: !deal.ongoingEndDate && !deal.lastInvoiceDate
            };
        }

        if (model === 'milestone') {
            const milestones = deal.dealMilestones || [];
            const contractValue = milestones.reduce((s, m) => s + (Number(m.amount) || 0), 0);
            return { totalInvoices: milestones.length, contractValue };
        }

        if (model === 'custom') {
            const rows = deal.customScheduleRows || [];
            const contractValue = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
            return { totalInvoices: rows.length, contractValue };
        }

        if (model === 'one_time') {
            const amount = Number(deal.invoiceAmount) || 0;
            return { totalInvoices: 1, contractValue: amount };
        }

        if (!deal.expectedFirstInvoiceDate || !deal.lastInvoiceDate) {
            return {
                totalInvoices: null,
                contractValue: Number(deal.contractValue) || 0,
                openEnded: !deal.lastInvoiceDate
            };
        }

        const frequency = deal.invoiceFrequency || paymentModelToFrequency(model);
        const totalInvoices = countInvoices(
            deal.expectedFirstInvoiceDate,
            deal.lastInvoiceDate,
            frequency
        );
        const contractValue = totalInvoices * (Number(deal.invoiceAmount) || 0);

        return {
            totalInvoices,
            contractValue
        };
    }

    /**
     * Get date range for time period
     */
    static getDateRange(period) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let startDate, endDate;

        switch (period) {
            case 'thisMonth':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;

            case 'nextMonth':
                startDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                break;

            case 'thisQuarter': {
                const quarter = Math.floor(today.getMonth() / 3);
                startDate = new Date(today.getFullYear(), quarter * 3, 1);
                endDate = new Date(today.getFullYear(), quarter * 3 + 3, 0);
                break;
            }

            case 'nextQuarter': {
                const quarter = Math.floor(today.getMonth() / 3);
                const nextQuarter = quarter + 1;
                startDate = new Date(today.getFullYear(), nextQuarter * 3, 1);
                endDate = new Date(today.getFullYear(), nextQuarter * 3 + 3, 0);
                break;
            }

            case 'thisYear':
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = new Date(today.getFullYear(), 11, 31);
                break;

            case 'nextYear':
                startDate = new Date(today.getFullYear() + 1, 0, 1);
                endDate = new Date(today.getFullYear() + 1, 11, 31);
                break;

            case 'pastYear':
                startDate = new Date(today.getFullYear() - 1, 0, 1);
                endDate = new Date(today.getFullYear() - 1, 11, 31);
                break;

            default:
                startDate = new Date(today.getFullYear(), 0, 1);
                endDate = new Date(today.getFullYear() + 5, 11, 31);
        }

        return { startDate, endDate };
    }

    /**
     * Calculate revenue metrics respecting global filters
     */
    static async calculateMetrics(filters = {}) {
        let start;
        let end;
        if (filters.startDate && filters.endDate) {
            start = formatDateLocal(new Date(filters.startDate));
            end = formatDateLocal(new Date(filters.endDate));
        } else {
            const period = RevenueCalculationService.getDateRange(filters.period || filters.periodType || 'thisYear');
            start = formatDateLocal(period.startDate);
            end = formatDateLocal(period.endDate);
        }
        const needsClientFilter = !!(filters.pipelineIds?.length || filters.leadSources?.length);

        if (!needsClientFilter) {
            try {
                const params = { start, end };
                if (filters.ownerUserId) params.owner_user_id = filters.ownerUserId;
                const data = await ApiClient.get('/api/v1/dashboard/widget-data/revenue_period_metrics', params);
                if (data && typeof data === 'object' && 'plannedInPeriod' in data) {
                    return data;
                }
            } catch (err) {
                console.warn('[RevenueCalculationService] period metrics API failed; using client calc', err);
            }
        }

        let deals = await CrmApiRepository.getAllDealsListView();
        let schedules = await CrmApiRepository.getAllSchedules();
        if (filters.ownerUserId || filters.pipelineIds?.length || filters.leadSources?.length) {
            const allowed = new Set(deals.filter((d) => {
                if (filters.ownerUserId && (d.ownerUserId || d.owner_user_id) !== filters.ownerUserId) return false;
                if (!DashboardMetricsService.matchesPipelineFilter(d, filters.pipelineIds)) return false;
                if (!DashboardMetricsService.matchesLeadSourceFilter(d, filters.leadSources)) return false;
                return true;
            }).map((d) => d.id));
            deals = deals.filter((d) => allowed.has(d.id));
            schedules = schedules.filter((s) => allowed.has(s.dealId || s.deal_id));
        }
        return RevenueCalculationService.metricsFrom(schedules, deals, filters);
    }

    static metricsFrom(schedules, deals, filters = {}) {
        const period = aggregateRevenueMetrics(schedules, deals, filters);
        const todayKey = formatDateLocal(new Date());

        const metrics = {
            plannedInPeriod: period.planned,
            forecastInPeriod: period.forecast,
            actualInPeriod: period.actual,
            achievementPercentage: period.achievement,
            forecastAccuracy: 0,
            committedRevenue: 0,
            atRiskRevenue: 0,
            lostRevenue: 0,
            plannedThisYear: period.planned,
            totalActual: period.actual
        };

        const dealsWithIssuedSchedules = new Set();

        schedules.forEach(schedule => {
            const status = schedule.status || 'planned';
            if (['invoiced', 'issued', 'paid'].includes(status)) {
                dealsWithIssuedSchedules.add(schedule.dealId);
            }

            if (!scheduleInPeriod(schedule, filters)) return;
            const paidAmount = schedule.paidAmount || 0;
            const amt = schedule.plannedAmount || 0;
            const invoiceKey = String(schedule.invoiceDate || '').slice(0, 10);

            if (status === 'invoiced' || status === 'issued') {
                metrics.committedRevenue += amt;
            } else if (status === 'cancelled') {
                metrics.lostRevenue += amt;
            } else if (status === 'at_risk' || status === 'overdue' || (invoiceKey && invoiceKey < todayKey && paidAmount === 0 && status !== 'paid')) {
                metrics.atRiskRevenue += amt;
            }
        });

        deals.forEach(deal => {
            if (deal.status !== 'won') return;
            if (dealsWithIssuedSchedules.has(deal.id)) return;
            const recognition = {
                invoiceDate: deal.expectedCloseDate || deal.closedAt || deal.closed_at || deal.createdAt || deal.created_at
            };
            if (!scheduleInPeriod(recognition, filters)) return;
            metrics.committedRevenue += deal.contractValue || deal.dealAmount || deal.amount || 0;
        });

        if (metrics.forecastInPeriod > 0) {
            metrics.forecastAccuracy = Math.round((metrics.actualInPeriod / metrics.forecastInPeriod) * 100);
        }

        return metrics;
    }

    /**
     * Generate monthly forecast table
     */
    static async generateMonthlyForecast(startDate, endDate) {
        const schedules = await CrmApiRepository.getSchedulesByDateRange(
            startDate.toISOString().split('T')[0],
            endDate.toISOString().split('T')[0]
        );

        const monthlyData = {};

        schedules.forEach(schedule => {
            const monthKey = String(schedule.invoiceDate || '').slice(0, 7);
            if (!monthKey) return;

            if (!monthlyData[monthKey]) {
                monthlyData[monthKey] = {
                    month: monthKey,
                    planned: 0,
                    actual: 0,
                    paid: 0,
                    variance: 0,
                    achievement: 0
                };
            }

            monthlyData[monthKey].planned += schedule.plannedAmount;
            monthlyData[monthKey].actual += schedule.actualAmount || 0;
            monthlyData[monthKey].paid += schedule.paidAmount || 0;
        });

        Object.keys(monthlyData).forEach(key => {
            const month = monthlyData[key];
            const collected = month.actual + month.paid;
            month.variance = collected - month.planned;
            month.achievement = month.planned > 0
                ? Math.round((collected / month.planned) * 100)
                : 0;
        });

        return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
    }

    /**
     * Generate quarterly forecast table
     */
    static async generateQuarterlyForecast(year) {
        const schedules = await CrmApiRepository.getAllSchedules();

        const quarterlyData = {
            'Q1': { quarter: 'Q1', planned: 0, actual: 0, paid: 0, variance: 0, achievement: 0 },
            'Q2': { quarter: 'Q2', planned: 0, actual: 0, paid: 0, variance: 0, achievement: 0 },
            'Q3': { quarter: 'Q3', planned: 0, actual: 0, paid: 0, variance: 0, achievement: 0 },
            'Q4': { quarter: 'Q4', planned: 0, actual: 0, paid: 0, variance: 0, achievement: 0 }
        };

        schedules.forEach(schedule => {
            const dateStr = String(schedule.invoiceDate || '');
            const yearPart = Number(dateStr.slice(0, 4));
            const monthPart = Number(dateStr.slice(5, 7));
            if (yearPart === year && monthPart >= 1 && monthPart <= 12) {
                const quarterKey = ['Q1', 'Q2', 'Q3', 'Q4'][Math.floor((monthPart - 1) / 3)];
                quarterlyData[quarterKey].planned += schedule.plannedAmount || 0;
                quarterlyData[quarterKey].actual += schedule.actualAmount || 0;
                quarterlyData[quarterKey].paid += schedule.paidAmount || 0;
            }
        });

        Object.keys(quarterlyData).forEach(key => {
            const quarter = quarterlyData[key];
            const collected = quarter.actual + quarter.paid;
            quarter.variance = collected - quarter.planned;
            quarter.achievement = quarter.planned > 0
                ? Math.round((collected / quarter.planned) * 100)
                : 0;
        });

        return Object.values(quarterlyData);
    }

    /**
     * Generate yearly forecast table
     */
    static async generateYearlyForecast(startYear, endYear) {
        const schedules = await CrmApiRepository.getAllSchedules();

        const yearlyData = {};

        for (let year = startYear; year <= endYear; year++) {
            yearlyData[year] = {
                year: year.toString(),
                planned: 0,
                actual: 0,
                paid: 0,
                variance: 0,
                achievement: 0
            };
        }

        schedules.forEach(schedule => {
            const year = Number(String(schedule.invoiceDate || '').slice(0, 4));

            if (yearlyData[year]) {
                yearlyData[year].planned += schedule.plannedAmount;
                yearlyData[year].actual += schedule.actualAmount || 0;
                yearlyData[year].paid += schedule.paidAmount || 0;
            }
        });

        Object.keys(yearlyData).forEach(key => {
            const year = yearlyData[key];
            const collected = year.actual + year.paid;
            year.variance = collected - year.planned;
            year.achievement = year.planned > 0
                ? Math.round((collected / year.planned) * 100)
                : 0;
        });

        return Object.values(yearlyData).sort((a, b) => a.year.localeCompare(b.year));
    }

    /**
     * Generate monthly heatmap data
     */
    static async generateMonthlyHeatmap() {
        const schedules = await CrmApiRepository.getAllSchedules();

        const currentYear = new Date().getFullYear();
        const monthlyAmounts = {};

        for (let month = 0; month < 12; month++) {
            const monthKey = `${currentYear}-${String(month + 1).padStart(2, '0')}`;
            monthlyAmounts[monthKey] = 0;
        }

        schedules.forEach(schedule => {
            const dateStr = String(schedule.invoiceDate || '');
            if (Number(dateStr.slice(0, 4)) === currentYear) {
                const monthKey = dateStr.slice(0, 7);
                if (monthlyAmounts[monthKey] !== undefined) {
                    monthlyAmounts[monthKey] += schedule.plannedAmount;
                }
            }
        });

        // Normalize to intensity levels
        const values = Object.values(monthlyAmounts);
        const maxValue = Math.max(...values);
        const minValue = Math.min(...values);
        const range = maxValue - minValue || 1;

        const heatmap = [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        for (let month = 0; month < 12; month++) {
            const monthKey = `${currentYear}-${String(month + 1).padStart(2, '0')}`;
            const value = monthlyAmounts[monthKey];
            const intensity = Math.floor(((value - minValue) / range) * 4);

            heatmap.push({
                month: monthNames[month],
                amount: value,
                intensity: minValue === maxValue ? 0 : intensity
            });
        }

        return heatmap;
    }
}
